/**
 * Simular un mensaje de esta persona (sólo con el servidor en modo simulado, §4.5 y §16). Pasa por el mismo
 * camino que un webhook real, así que la bandeja se prueba entera: texto, foto, audio, documento y
 * ubicación, y «Vencer la ventana» para probar la reapertura con plantilla sin esperar 24 h.
 *
 * Nadie recibe nada. «Vencer la ventana» se ofrece sólo en una conversación simulada: el backend además la
 * rechaza en una real (con el servidor en simulado, el filtro de modo no separa las reales).
 *
 * Desde `xl` arranca plegada: va en la columna del alto de la pantalla, abajo de la caja, y abierta (246 px) le dejaba
 * al hilo el mínimo y apretaba la ficha, justo en las cuentas de demo, que son las que se muestran. Se despliega EN LA
 * PÁGINA con un toque. En el celular va abierta, al final de la página, como siempre.
 */

import { useId, useRef, useState } from "react";
import { ChevronDown, ChevronUp, FlaskConical, Loader2 } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { SimularEntranteBody } from "../../conversacionesApi";
import type { ConversacionDetalle } from "../../types.conversaciones";
import type { SimularInboundResultado } from "../../types";
import { llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";
import { esComputadora } from "../../logic/conversaciones";

const UBICACION_DE_PRUEBA = { latitud: -31.4201, longitud: -64.1888, direccion: "Av. Colón 1000, Córdoba", etiqueta: "Local" };

export function SimularEnHilo({ conversacion: c, onSimulado }: { conversacion: ConversacionDetalle; onSimulado: () => void }) {
  const id = useId();
  const [texto, setTexto] = useState("");
  const [foto, setFoto] = useState(false);
  const [audio, setAudio] = useState(false);
  const [documento, setDocumento] = useState(false);
  const [ubicacion, setUbicacion] = useState(false);
  const [enCurso, setEnCurso] = useState<"" | "simular" | "vencer">("");
  const [error, setError] = useState("");
  const [abierta, setAbierta] = useState(() => !esComputadora());
  const ocupado = useRef(false);

  const hayAlgo = !!texto.trim() || foto || audio || documento || ubicacion;

  const simular = async () => {
    if (ocupado.current || !hayAlgo) return;
    ocupado.current = true;
    setEnCurso("simular");
    setError("");
    const medios = [foto && "image/png", audio && "audio/ogg", documento && "application/pdf"].filter((x): x is string => !!x).map((contentType) => ({ contentType }));
    const cuerpo: SimularEntranteBody = {
      ...(c.numero ? { from: c.numero } : { sinTelefono: true, ...(c.usuario ? { externalUserId: c.usuario } : {}) }),
      ...(texto.trim() ? { body: texto.trim() } : {}),
      ...(medios.length ? { medios } : {}),
      ...(ubicacion ? { ubicacion: UBICACION_DE_PRUEBA } : {}),
    };
    const l = await llamar<SimularInboundResultado>(() => conversacionesApi.simularEntrante(cuerpo));
    ocupado.current = false;
    setEnCurso("");
    if (!l.ok) { setError(textoError(l)); return; }
    setTexto("");
    setFoto(false); setAudio(false); setDocumento(false); setUbicacion(false);
    onSimulado();
  };

  const vencer = async () => {
    if (ocupado.current) return;
    ocupado.current = true;
    setEnCurso("vencer");
    setError("");
    const l = await llamar<{ ultimoEntranteAt: string }>(() => conversacionesApi.simularVencerVentana(c.id));
    ocupado.current = false;
    setEnCurso("");
    if (!l.ok) { setError(textoError(l)); return; }
    onSimulado();
  };

  const casilla = (label: string, v: boolean, set: (x: boolean) => void) => (
    <label className="inline-flex items-center gap-1.5">
      <input type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} /> {label}
    </label>
  );

  return (
    <section className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/40 p-4 text-sm text-slate-700 space-y-2 xl:p-3" aria-labelledby={`${id}-titulo`}>
      <h3 id={`${id}-titulo`} className="font-semibold text-slate-900">
        <button type="button" onClick={() => setAbierta((x) => !x)} aria-expanded={abierta} className="inline-flex items-center gap-1.5 text-left">
          <FlaskConical size={14} className="text-amber-600" /> Simular un mensaje de esta persona (modo simulado)
          {abierta ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </h3>
      {abierta && (
        <>
          <label htmlFor={`${id}-texto`} className="block text-xs font-semibold text-slate-600">Texto del mensaje</label>
          <textarea id={`${id}-texto`} value={texto} rows={2} maxLength={4096} onChange={(e) => setTexto(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {casilla("Con foto", foto, setFoto)}
            {casilla("Con audio", audio, setAudio)}
            {casilla("Con documento", documento, setDocumento)}
            {casilla("Con ubicación", ubicacion, setUbicacion)}
          </div>
          {error && <p role="alert" className="text-red-700">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void simular()} disabled={!!enCurso || !hayAlgo}
              className="h-9 px-4 rounded-xl bg-amber-600 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {enCurso === "simular" && <Loader2 size={14} className="animate-spin" />} Simular
            </button>
            {c.simulado && (
              <button type="button" onClick={() => void vencer()} disabled={!!enCurso}
                className="h-9 px-4 rounded-xl border border-amber-300 bg-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                {enCurso === "vencer" && <Loader2 size={14} className="animate-spin" />} Vencer la ventana de 24 h (simulado)
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">Nadie recibe nada: es para probar la bandeja.</p>
        </>
      )}
    </section>
  );
}

export default SimularEnHilo;
